import { PrioritizedClientCaseDefinition } from '@api/generated/models';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { AttentionCardController } from '@components/attention_card/_attention_card.controller';
import { useUser } from '@services/auth/use_user';
import { useService } from '@visiba/instantiation';
import { usePresentation } from '@visiba/presentation';
import { useEffect } from 'react';
import styled from 'styled-components';
import { AnalyticProvider, useAnalyticContext } from '../analytic_link/mod';
import { AttentionCardTypeCurrentAppointment } from './_attention_card_type_current_appointment.component';
import { AttentionCardTypeNewMessage } from './_attention_card_type_new_message.component';
import { AttentionCardTypeNewMessagesDifferentCases } from './_attention_card_type_new_messages_different_cases.component';
import { AttentionCardTypeUnansweredForms } from './_attention_card_type_unanswered_forms.component';
import { AttentionCardTypeUnpaid } from './_attention_card_type_unpaid.component';
import { AttentionCardTypeUpcomingAppointments } from './_attention_card_type_upcoming_appointment.component';

interface Props {}

const elements = {
	container: styled.div`
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		pointer-events: none;
		z-index: 1;
	`,
};

export function AttentionCard(_: Props): JSX.Element | null {
	const user = useUser();

	if (user == null) {
		return null;
	}

	return (
		<AnalyticProvider category={AnalyticCategories.AttentionCards}>
			<elements.container>
				<Inner />
			</elements.container>
		</AnalyticProvider>
	);
}

function Inner(_: Props): JSX.Element | null {
	const analyticService = useService(AnalyticService);
	const controller = useService(AttentionCardController);
	const presentation = usePresentation(controller.presentation);
	const category = useAnalyticContext();

	useEffect(
		function whenComponentUnmounts() {
			return () => {
				controller.teardown();
			};
		},
		[controller],
	);

	useEffect(
		function analytic_whenPresentationTypeChanges() {
			const action = controller.mapEnumToAnalyticEventAction(presentation.type);
			if (action != null) {
				analyticService.trackEvent(category, action);
			}
		},
		[controller, analyticService, category, presentation.type],
	);

	switch (presentation.type) {
		case PrioritizedClientCaseDefinition.None:
			return null;
		case PrioritizedClientCaseDefinition.UnansweredForms:
			return <AttentionCardTypeUnansweredForms case={presentation.case} unansweredFormsNames={presentation.unansweredFormsNames} />;
		case PrioritizedClientCaseDefinition.Unpaid:
			return <AttentionCardTypeUnpaid case={presentation.appointmentCase} />;
		case PrioritizedClientCaseDefinition.NewMessages:
			return <AttentionCardTypeNewMessage message={presentation.messageCase} />;
		case PrioritizedClientCaseDefinition.NewMessagesDifferentCases:
			return <AttentionCardTypeNewMessagesDifferentCases message={presentation.messageCase} />;
		case PrioritizedClientCaseDefinition.CurrentAppointment:
			return <AttentionCardTypeCurrentAppointment case={presentation.appointmentCase} />;
		case PrioritizedClientCaseDefinition.UpcomingAppointment:
			return <AttentionCardTypeUpcomingAppointments case={presentation.appointmentCase} />;
		default:
			return null;
	}
}
